// react
import React, { useState } from "react";

// react-router-dom
import { useNavigate } from "react-router-dom";

// context
import { UserAuth } from "context/AuthContext";

// react-i18next
import { useTranslation } from "react-i18next";

// react-flagkit
import Flag from "react-flagkit";

// @mui
import { useTheme, useMediaQuery, Link, Box, Grid, Paper, AppBar, Toolbar, Typography, IconButton, Tooltip, Select, MenuItem, BottomNavigation, BottomNavigationAction } from "@mui/material";
import { Logout as LogoutIcon, AccountCircle as AccountCircleIcon, Archive as ArchiveIcon, Groups as GroupsIcon, QrCodeScanner as QrCodeScannerIcon } from "@mui/icons-material";

// ui-components
import ArmilisSidebar from "./ArmilisSidebar";
import QRCodeScanner from "ui-components/databox/QRCodeScanner";

// A ---------------------------------------------------------------------- M

const Logo = () => (
  <Link href="/" sx={{ display: "flex" }}>
    <Box
      component="img"
      src="/logo/trusted-operations/logo.png"
      alt="TrustedOperations logo"
      sx={{
        maxHeight: 30,
        transform: "scale(4.5)",
        transformOrigin: "left",
      }}
    />
  </Link>
);

const LanguageOption = ({ country, label }) => (
  <Box display="flex" alignItems="center">
    <Flag country={country} style={{ marginRight: 8 }} />
    {label}
  </Box>
);

const bottomNavRoutes = ["/", "/my-groups", "/my-account", "/qr-scanner"];

const ArmilisLayout = ({ children }) => {
  const { user, language, setLanguage, logout } = UserAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [openQRCodeScanner, setOpenQRCodeScanner] = useState(false);
  const [bottomNavValue, setBottomNavValue] = useState(0);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/signin");
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const handleMyAccount = () => navigate("/my-account");
  const handleLanguageChange = (e) => setLanguage(e.target.value);

  const openQRCodeScannerDrawer = () => {
    setOpenQRCodeScanner(true);
    window.history.pushState(null, "");
  };

  const handleBottomNavChange = (event, newValue) => {
    setBottomNavValue(newValue);
    const route = bottomNavRoutes[newValue];
    if (route === "/qr-scanner") {
      openQRCodeScannerDrawer();
    } else {
      navigate(route);
    }
  };

  const drawerWidth = 230;
  const gridItemStyle = isMobile ? { width: "100%", p: 2, pb: 8 } : { width: `calc(100% - ${drawerWidth}px)`, ml: `${drawerWidth}px`, p: 3 };

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <AppBar
            position="fixed"
            elevation={0}
            sx={{
              zIndex: theme.zIndex.drawer + 1,
              background: "linear-gradient(180deg, rgba(11,11,15,0.9) 0%, rgba(5,5,8,0.98) 100%)",
              backdropFilter: "blur(10px)",
              borderBottom: "1px solid rgba(255,255,255,0.08)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
            }}
          >
            <Toolbar variant="regular">
              <Logo />
              <Box sx={{ flexGrow: 1 }} />
              <Select
                value={language}
                onChange={handleLanguageChange}
                variant="standard"
                disableUnderline
                sx={{
                  color: "#ffffff",
                  "& .MuiSvgIcon-root": { color: "#ffffff" },
                }}
              >
                <MenuItem value="it">
                  <LanguageOption country="IT" label="IT" />
                </MenuItem>
                <MenuItem value="en">
                  <LanguageOption country="GB" label="EN" />
                </MenuItem>
              </Select>

              {!isMobile && user && (
                <Typography
                  variant="body2"
                  sx={{
                    mx: 2,
                    fontWeight: 600,
                    opacity: 0.8,
                    maxWidth: "250px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={user.email}
                >
                  {user.email}
                </Typography>
              )}

              {!isMobile && user && (
                <Tooltip title={t("my_account")}>
                  <IconButton onClick={handleMyAccount} sx={{ color: "#ffffff" }}>
                    <AccountCircleIcon />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title={t("logout")}>
                <IconButton onClick={handleLogout} sx={{ color: "#ffffff" }}>
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            </Toolbar>
          </AppBar>
          <Toolbar />
        </Grid>

        <Grid item sx={gridItemStyle}>
          {children}
        </Grid>

        {isMobile && (
          <Paper
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              bgcolor: "rgba(255,255,255,0.9)",
              backdropFilter: "blur(6px)",
              boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
              pb: 4,
              pt: 0.5,
            }}
          >
            <BottomNavigation
              showLabels
              value={bottomNavValue}
              onChange={handleBottomNavChange}
              sx={{
                "& .MuiBottomNavigationAction-root": {
                  color: theme.palette.text.primary,
                  minHeight: 56,
                  "&.Mui-selected": {
                    color: theme.palette.primary.main,
                  },
                },
              }}
            >
              <BottomNavigationAction label={t("my_databoxes")} icon={<ArchiveIcon />} />
              <BottomNavigationAction label={t("my_groups")} icon={<GroupsIcon />} />
              <BottomNavigationAction label={t("account")} icon={<AccountCircleIcon />} />
              <BottomNavigationAction label={t("scan")} icon={<QrCodeScannerIcon />} />
            </BottomNavigation>
          </Paper>
        )}
      </Grid>

      {!isMobile && <ArmilisSidebar drawerWidth={drawerWidth} openQRCodeScannerDrawer={openQRCodeScannerDrawer} />}

      {isMobile && <QRCodeScanner open={openQRCodeScanner} setOpen={setOpenQRCodeScanner} />}
    </>
  );
};

export default ArmilisLayout;
